<template>
  <!--
    La ficha de un registro que todavía no ha llegado (un cliente, una obra,
    una orden), con la anatomía de las fichas del ERP de arriba abajo:

      ← volver
      Título grande                         [acción] [acción]
      segunda línea (código, NIF)
      [cifra] [cifra] [cifra]               ← `stats`, si la ficha las lleva
      Pestaña · Pestaña · Pestaña           ← `tabs`, si las lleva
      ┌ Tarjeta ───────────────────────┐
      │ rótulo        valor            │    ← `fields` pares por tarjeta
      └────────────────────────────────┘

    Sustituye a la ficha ENTERA mientras carga, cabecera incluida: el título
    de una ficha es un dato (el nombre del cliente) y no existe hasta que
    llega. Si la pantalla ya dibuja su UiPageHeader sin esperar, `:header=false`.
  -->
  <div class="sk-file" role="status" aria-busy="true">
    <span class="sr-only">{{ uiLabel('loading') }}</span>

    <div v-if="header" class="sk-file__header" aria-hidden="true">
      <div class="sk-file__heading">
        <span v-if="back" class="sk-file__back"><UiSkeleton height="0.8em" width="84px" /></span>
        <span class="sk-file__title"><UiSkeleton height="70%" width="min(320px, 72%)" /></span>
        <span v-if="subtitle" class="sk-file__subtitle"><UiSkeleton height="0.8em" width="120px" /></span>
      </div>
      <div v-if="actions" class="sk-file__actions">
        <UiSkeleton
          v-for="action in actions"
          :key="action"
          variant="button"
          :width="ACTION[action % ACTION.length]"
        />
      </div>
    </div>

    <UiSkeletonCards v-if="stats" class="sk-file__stats" :count="stats" quiet />

    <div v-if="tabs" class="sk-file__tabs" aria-hidden="true">
      <span v-for="tab in tabs" :key="tab" class="sk-file__tab" :class="{ 'sk-file__tab--on': tab === 1 }">
        <UiSkeleton height="0.85em" :width="TAB[tab % TAB.length]" />
      </span>
    </div>

    <section v-for="section in sections" :key="section" class="sk-file__card" aria-hidden="true">
      <header class="sk-file__card-head">
        <UiSkeleton height="1em" :width="CARD_TITLE[section % CARD_TITLE.length]" />
      </header>
      <div class="sk-file__pairs">
        <div v-for="field in fields" :key="field" class="sk-file__pair">
          <span class="sk-file__line sk-file__label">
            <UiSkeleton height="0.75em" :width="LABEL[(field + section) % LABEL.length]" />
          </span>
          <span class="sk-file__line">
            <UiSkeleton :width="VALUE[(field * 2 + section) % VALUE.length]" />
          </span>
        </div>
      </div>
    </section>
  </div>
</template>

<script setup>
import UiSkeleton from './UiSkeleton.vue'
import UiSkeletonCards from './UiSkeletonCards.vue'
import { uiLabel } from '../utils/labels'

defineProps({
  // La cabecera de la ficha (volver, título, segunda línea y botones).
  header: { type: Boolean, default: true },
  back: { type: Boolean, default: true },
  subtitle: { type: Boolean, default: true },
  // Cuántos botones lleva la cabecera (Editar, Archivar…). 0 para ninguno.
  actions: { type: Number, default: 2 },
  // Cuántas cifras (UiStat) hay debajo de la cabecera. 0 para ninguna.
  stats: { type: Number, default: 0 },
  // Cuántas pestañas (UiTabs). 0 si la ficha no las tiene.
  tabs: { type: Number, default: 3 },
  // Tarjetas de datos y pares rótulo/valor en cada una.
  sections: { type: Number, default: 1 },
  fields: { type: Number, default: 6 },
})

const ACTION = ['88px', '104px', '96px']
const TAB = ['56px', '84px', '70px', '92px', '64px']
const CARD_TITLE = ['34%', '28%', '40%']
const LABEL = ['46%', '58%', '40%', '52%']
const VALUE = ['62%', '44%', '78%', '54%', '68%']
</script>

<style scoped>
.sk-file { display: flex; flex-direction: column; gap: var(--space-5); }

/* ── La cabecera: medidas de UiPageHeader ───────────────────────────────── */
.sk-file__header {
  display: flex;
  align-items: flex-start;
  flex-wrap: wrap;
  gap: var(--space-4);
  /* UiPageHeader deja `--space-6` debajo; el `gap` de arriba ya pone
     `--space-5` y aquí va el resto. */
  margin-bottom: var(--space-1);
}
.sk-file__heading { display: flex; flex-direction: column; flex: 1 1 18rem; min-width: 0; }
.sk-file__back { display: flex; align-items: center; height: calc(var(--text-sm) * 1.55); margin-bottom: var(--space-2); }
.sk-file__title { display: flex; align-items: center; height: calc(var(--text-2xl) * 1.25); }
.sk-file__subtitle { display: flex; align-items: center; height: calc(var(--text-sm) * 1.55); margin-top: var(--space-1); }
.sk-file__actions { display: flex; flex-wrap: wrap; gap: var(--space-2); }

/* ── Las pestañas: la fila de UiTabs con su raya debajo ─────────────────── */
.sk-file__tabs {
  display: flex;
  gap: var(--space-2);
  overflow: hidden;
  border-bottom: 1px solid var(--color-border);
}
.sk-file__tab {
  display: flex;
  align-items: center;
  flex: none;
  height: calc(var(--text-md) * 1.55 + var(--space-2) + var(--space-3) + 2px);
  padding: 0 var(--space-3);
  border-bottom: 2px solid transparent;
}
.sk-file__tab--on { border-bottom-color: var(--skeleton-base); }

/* ── Una tarjeta de datos: la piel de UiCard ────────────────────────────── */
.sk-file__card {
  background: var(--color-surface);
  border: 1px solid var(--color-border);
  border-radius: var(--radius-lg);
  box-shadow: var(--shadow-sm);
  overflow: hidden;
}
.sk-file__card-head {
  display: flex;
  align-items: center;
  min-height: calc(var(--text-lg) * 1.25 + 2 * var(--space-4));
  padding: var(--space-4) var(--space-5);
  border-bottom: 1px solid var(--color-border);
}
/* Los pares: rótulo en el 38 % de la izquierda, valor al lado; en una caja
   ancha, dos columnas de pares como las fichas de escritorio. */
.sk-file__pairs {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(min(22rem, 100%), 1fr));
  column-gap: var(--space-8);
  row-gap: var(--space-3);
  padding: var(--space-5) var(--space-5) var(--space-6);
}
.sk-file__pair { display: flex; gap: var(--space-3); min-width: 0; }
.sk-file__line { display: flex; align-items: center; flex: 1; min-width: 0; height: calc(var(--text-md) * 1.55); }
.sk-file__label { flex: 0 0 38%; }

/* En el teléfono la cabecera de una ficha es más corta (UiPageHeader). */
@media (max-width: 640px) {
  .sk-file { gap: var(--space-4); }
  .sk-file__header { gap: var(--space-3); margin-bottom: 0; }
  .sk-file__title { height: calc(var(--text-xl) * 1.25); }
}
</style>
